import * as React from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import {
  GooglePlayButton,
  AppStoreButton,
  ButtonsContainer,
} from 'react-mobile-app-button';
import { motion, useScroll, useMotionValueEvent, useReducedMotion } from 'framer-motion';
import { RevealHeading } from '../components/motion';
import { colors } from '../theme';

const APK = 'https://play.google.com/store/apps/details?id=com.chesadrian.markethours';
const IOS = 'https://apps.apple.com/ro/app/markethours/id6478150068';

const features = [
  { icon: <EmojiEventsRoundedIcon />, title: 'Top Markets',
    text: 'Instantly see the trading hours of the world’s top markets.', image: '/top.jpeg' },
  { icon: <BoltRoundedIcon />, title: 'Blazing Fast',
    text: 'Market hours are defined statically, so the app is blazing fast — no waiting.', image: '/topy.jpeg' },
  { icon: <StarRoundedIcon />, title: 'Your Favorites',
    text: 'Pick your favorite markets and dive deeper into the ones you care about.', image: '/favorites.jpeg' },
];

function StoreButtons() {
  return (
    <ButtonsContainer>
      <GooglePlayButton url={APK} theme="light" className="custom-style" />
      <AppStoreButton url={IOS} theme="light" className="custom-style" />
    </ButtonsContainer>
  );
}

function PhoneFrame({ children, sx }) {
  return (
    <Box
      sx={{
        position: 'relative',
        width: { xs: 240, sm: 280 },
        aspectRatio: '1290 / 2796',
        mx: 'auto',
        borderRadius: '44px',
        p: '9px',
        background: 'linear-gradient(160deg, #2a2a2e, #050506)',
        boxShadow: '0 40px 80px -30px rgba(0,0,0,0.55), inset 0 0 0 2px rgba(255,255,255,0.06)',
        ...sx,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 16,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 92,
          height: 24,
          borderRadius: 999,
          backgroundColor: '#050506',
          zIndex: 2,
        }}
      />
      <Box sx={{ position: 'relative', width: '100%', height: '100%', borderRadius: '36px', overflow: 'hidden', backgroundColor: '#000' }}>
        {children}
      </Box>
    </Box>
  );
}

function Screens({ active }) {
  return (
    <>
      {features.map((f, i) => (
        <Box
          key={i}
          component={motion.div}
          initial={false}
          animate={{ opacity: active === i ? 1 : 0, scale: active === i ? 1 : 1.05 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `url("${f.image}")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
      ))}
    </>
  );
}

function Intro() {
  return (
    <Box sx={{ textAlign: 'center', maxWidth: 820, mx: 'auto', mb: { xs: 6, md: 2 } }}>
      <Typography variant="overline" sx={{ color: colors.blue, letterSpacing: '0.24em' }}>
        MARKET HOURS
      </Typography>
      <RevealHeading
        variant="h2"
        sx={{ mt: 2 }}
        segments={[{ text: 'Never miss the open.' }]}
      />
      <Typography variant="subtitle1" sx={{ color: colors.inkSoft, mt: 2 }}>
        A mobile app that combats the fear of missing out on the market hours.
      </Typography>
    </Box>
  );
}

/* Desktop: pinned phone, scroll scrubs through features */
function StickyShowcase() {
  const ref = React.useRef(null);
  const [active, setActive] = React.useState(0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end end'] });

  useMotionValueEvent(scrollYProgress, 'change', (v) => {
    const idx = Math.min(features.length - 1, Math.max(0, Math.floor(v * features.length)));
    setActive(idx);
  });

  return (
    <Box ref={ref} sx={{ height: `${features.length * 100}vh`, position: 'relative' }}>
      <Box sx={{ position: 'sticky', top: 0, height: '100vh', display: 'flex', alignItems: 'center' }}>
        <Container>
          <Grid container spacing={8} alignItems="center">
            <Grid item md={6}>
              <Stack spacing={3}>
                {features.map((f, i) => (
                  <Box
                    key={i}
                    component={motion.div}
                    animate={{ opacity: active === i ? 1 : 0.32, x: active === i ? 0 : -6 }}
                    transition={{ duration: 0.4 }}
                    sx={{ display: 'flex', gap: 2.5 }}
                  >
                    <Box sx={{ color: active === i ? colors.blue : colors.grayBorder, '& svg': { fontSize: 30 } }}>
                      {f.icon}
                    </Box>
                    <Box>
                      <Typography variant="h5" sx={{ mb: 0.5 }}>{f.title}</Typography>
                      <Typography variant="body1" sx={{ color: colors.inkSoft }}>{f.text}</Typography>
                    </Box>
                  </Box>
                ))}
                <Box sx={{ pt: 2 }}><StoreButtons /></Box>
              </Stack>
            </Grid>
            <Grid item md={6} sx={{ display: 'flex', justifyContent: 'center' }}>
              <PhoneFrame><Screens active={active} /></PhoneFrame>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
}

/* Mobile / reduced motion: stacked, tappable */
function StackedShowcase() {
  const [active, setActive] = React.useState(0);
  return (
    <Container sx={{ pb: 10 }}>
      <PhoneFrame sx={{ mb: 4 }}><Screens active={active} /></PhoneFrame>
      <Stack spacing={2} sx={{ mb: 4 }}>
        {features.map((f, i) => (
          <Box
            key={i}
            onClick={() => setActive(i)}
            sx={{
              cursor: 'pointer',
              p: 2.5,
              borderRadius: 4,
              border: `1px solid ${active === i ? colors.blue : colors.grayBorder}`,
              backgroundColor: active === i ? 'rgba(0,113,227,0.06)' : 'transparent',
              display: 'flex',
              gap: 2,
            }}
          >
            <Box sx={{ color: active === i ? colors.blue : colors.inkSoft }}>{f.icon}</Box>
            <Box>
              <Typography variant="h6">{f.title}</Typography>
              <Typography variant="body2" sx={{ color: colors.inkSoft }}>{f.text}</Typography>
            </Box>
          </Box>
        ))}
      </Stack>
      <Box sx={{ display: 'flex', justifyContent: 'center' }}><StoreButtons /></Box>
    </Container>
  );
}

export default function MarketHours() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const reduce = useReducedMotion();

  return (
    <Box id="market-hours" sx={{ backgroundColor: colors.white, pt: { xs: 12, md: 20 } }}>
      <Intro />
      {isMobile || reduce ? <StackedShowcase /> : <StickyShowcase />}
    </Box>
  );
}
